import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export type MarkDeliveredRequest = {
	completedTime: string;
};

export const postMarkDelivered = async (
	poNumber: string,
	body: MarkDeliveredRequest,
) => {
	const resp = await postAndValidate(
		apiRoutes.POSTMARKDELIVERED.replace(":poNumber", poNumber),
		body,
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostMarkDelivered = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		unknown,
		Error,
		{ poNumber: string } & MarkDeliveredRequest
	>({
		mutationKey: ["markDelivered"],
		mutationFn: ({ poNumber, ...body }) => postMarkDelivered(poNumber, body),
		onSuccess: () => {
			showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentMarkedDelivered);
			// Return the invalidation promise so the mutation stays pending (and
			// the loading spinner stays visible) until the shipment query has
			// refetched with the updated state.
			return queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to mark shipment as delivered. Please try again.",
			);
		},
	});

	return {
		markDelivered: mutate,
		markDeliveredAsync: mutateAsync,
		isSavingMarkDelivered: isPending,
		error,
		data,
		resetError: reset,
	};
};
